import { dailyLoginPet, maxDailyLoginStreak, petDayIndex } from '../../Constants/DailyLogin';
import Game from '../../Game';
import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import { PlayerActions } from '../../State/Player/PlayerActions';
import { getHighestLevelCompleted } from '../../State/Player/PlayerLevelHelper';
import { scaleCoinRewardWithLevelCost } from '../../State/Player/PlayerStateHelper';
import { AnimationClipEvent } from '../../System/Animation/AnimationClip';
import { StartAnimationClip } from '../../System/Animation/AnimationHelper';
import { DayItemCollect } from '../../System/Animation/Clip/DayItemCollect';
import CleanupManager from '../../Utility/CleanupManager';
import DailyLoginHelper from '../../Utility/DailyLoginHelper';
import LevelHelper from '../../Utility/LevelHelper';
import { getRewardData, IRewardData } from '../../Utility/RewardHelper';
import { RewardType } from '../../Utility/Rewards/RewardType';
import Armature from '../Armature';
import Element, { IElementData } from './Element';

interface IDailyLoginItemParams extends IElementData {
    index: number;
    loginStreak: number;
    showPetInDaily: boolean;
}

const COLLECT_BUTTON_LOCATION: string = 'button_container/button';

export default class DayItem extends Element {
    private containerCleanupManager: CleanupManager = new CleanupManager();
    private loginStreak: number;
    private isLastDay: boolean;
    private giftArmature: Armature;
    private checkArmature: Armature;
    private isPetDay: boolean;
    public giftPetArmature: Armature;

    public onCreate(params: IDailyLoginItemParams): void {
        this.isLastDay = params.index === maxDailyLoginStreak;
        this.isPetDay = params.showPetInDaily && params.index === petDayIndex;
        this.loginStreak = params.loginStreak;
        super.onCreate({
            type: 'group',
            name: 'day_item',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                offsets: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
                ...params.layout,
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: this.getDayBackground(this.loginStreak, params.index),
                    },
                },
            ],
            children: [
                {
                    name: 'day_title',
                    layout: {
                        anchors: this.isLastDay ? [0.5, 0.11, 0.5, 0.11] : [0.5, 0.17, 0.5, 0.17],
                        pivot: [0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                text: this.getDayTitle(this.loginStreak, params.index),
                                style: this.isLastDay
                                    ? 'dailyLogin.popup.lastDayItemTitle'
                                    : 'dailyLogin.popup.dayItemTitle',
                                bounds: [250, 80],
                            },
                        },
                    ],
                },
                ...this.getGiftElements(params.index, this.loginStreak),

                {
                    name: 'button_container',
                    layout: {
                        anchors: [0.5, 0.85, 0.5, 0.85],
                        offsets: [0, 0, 0, 0],
                        pivot: [0.5, 0.5],
                    },
                    components: [
                        {
                            type: 'visibility',
                            data: {
                                hidden: this.loginStreak !== params.index,
                            },
                        },
                    ],
                    children: [
                        {
                            name: 'button',
                            layout: {
                                anchors: [0.5, 0.5, 0.5, 0.5],
                                offsets: [0, 0, 0, 0],
                                pivot: [0.5, 0.5],
                                scale: [1.25, 1.25],
                            },
                            components: [
                                {
                                    type: 'image',
                                    data: {
                                        cacheKey: 'daily_collect',
                                    },
                                },
                            ],
                            children: [
                                {
                                    name: 'DB_container',
                                    layout: {
                                        anchors: [0.5, 0.5, 0.5, 0.5],
                                        offsets: [5, 0, 5, 0],
                                        pivot: [0.5, 0.5],
                                    },
                                    components: [
                                        {
                                            type: 'image',
                                            data: {
                                                cacheKey: 'blank',
                                            },
                                        },
                                    ],
                                },
                                {
                                    name: 'text',
                                    layout: {
                                        anchors: [0.5, 0.5, 0.5, 0.5],
                                        offsets: [0, 0, 0, 0],
                                        pivot: [0.5, 0.5],
                                    },
                                    components: [
                                        {
                                            type: 'text',
                                            data: {
                                                textLocalizationKey: 'Collect',
                                                bounds: [100, 75],
                                                style: 'dailyLogin.popup.collectButton',
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    name: 'check_DB_container',
                    layout: {
                        anchors: this.isLastDay ? [0.5, 0.585, 0.5, 0.585] : [0.5, 0.73, 0.5, 0.73],
                        offsets: [0, 0, 0, 0],
                        pivot: [0.5, 0.5],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'blank',
                            },
                        },
                    ],
                },
            ],
        });

        const button = this.find(COLLECT_BUTTON_LOCATION);
        this.addDBAnimation(button, params.index, this.loginStreak);
    }

    public onCollectClicked(): Promise<IRewardData[]> {
        return new Promise(resolve => {
            const playerState = Game.store.getState().playerState;

            if (!this.isPetDay) {
                const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(playerState));
                // Get rewards and scale coin rewards with level cost
                const rewards = DailyLoginHelper.getRewardsFromPool(playerState).map(reward => {
                    return scaleCoinRewardWithLevelCost(reward, levelCost);
                });

                const rewardsContainer = this.find('rewards');
                for (let i = 0; i < rewards.length; i++) {
                    const element = rewards[i];
                    const reward = GameObject.Instantiate(Element, {
                        name: `reward_${i}`,
                        layout: {
                            anchors: [0.5, 0.5, 0.5, 0.5],
                            offsets: [0, 0, 0, 0],
                            pivot: [0.5, 0.5],
                            scale: [0.6, 0.6],
                        },
                        components: [
                            {
                                type: 'image',
                                data: {
                                    cacheKey: getRewardData(element.type).texture,
                                },
                            },
                        ],
                        children: [
                            {
                                name: 'amount_text',
                                layout: {
                                    anchors: [0.5, 1, 0.5, 1],
                                    offsets: [0, 0, 0, 0],
                                    pivot: [0.5, 0.5],
                                },
                                components: [
                                    {
                                        type: 'text',
                                        data: {
                                            text: 'x' + element.value,
                                            bounds: [200, 75],
                                            style: 'dailyLogin.popup.rewardAmount',
                                        },
                                    },
                                    {
                                        type: 'visibility',
                                        data: {
                                            hidden: true,
                                        },
                                    },
                                ],
                            },
                        ],
                    });
                    rewardsContainer.addChild(reward);
                }

                const giftContainer = this.findChildByName('gift_DB_container');
                const button = this.find(COLLECT_BUTTON_LOCATION);
                StartAnimationClip(
                    new DayItemCollect(giftContainer, {
                        button,
                        isLastDay: this.isLastDay,
                        rewardsContainer,
                        giftArmature: this.giftArmature,
                        checkArmature: this.checkArmature,
                    }).on(AnimationClipEvent.COMPLETE, () => {
                        resolve(rewards);
                    })
                );
            } else {
                resolve([
                    {
                        type: RewardType.PET,
                        value: 1,
                        petId: dailyLoginPet,
                    },
                ]);
            }
            Game.sdk.flushSaveData();
        });
    }

    public destroy() {
        this.containerCleanupManager.cleanUpAll();
        super.destroy();
    }
    private getDayTitle(loginStreak: number, index: number): string {
        if (loginStreak === index) {
            return Game.locale.getText('Today');
        } else if (index === loginStreak + 1) {
            return Game.locale.getText('Tomorrow');
        } else {
            return Game.locale.getText('DayX').replace('[x]', index.toString());
        }
    }

    private getDayBackground(loginStreak: number, index: number): string {
        if (this.isPetDay) {
            return 'daily_calendar_pet';
        } else if (index === maxDailyLoginStreak) {
            if (loginStreak === index) {
                return 'daily_calendar_4';
            } else {
                return 'daily_calendar_3';
            }
        } else if (loginStreak === index) {
            return 'daily_calendar_1';
        } else {
            return 'daily_calendar_2';
        }
    }
    private getGiftElements(index: number, loginStreak: number): IElementData[] {
        return this.isLastDay
            ? [
                  {
                      name: 'last_day_gift',
                      layout: {
                          anchors: [0.5, 0.585, 0.5, 0.585],
                          offsets: [0, 0, 0, 0],
                          pivot: [0.5, 0.5],
                      },
                      components: [
                          {
                              type: 'image',
                              data: {
                                  cacheKey: 'daily_day_final',
                              },
                          },
                      ],
                  },
                  this.addGiftDragonbonesContainer(),
                  this.addRewardsContainer(),
              ]
            : this.isPetDay
            ? [this.addGiftDragonbonesContainer()]
            : [
                  this.addRewardsContainer(),
                  {
                      name: 'day_gift_bottom',
                      layout: {
                          anchors: [0.5, 0.73, 0.5, 0.73],
                          offsets: [0, 0, 0, 0],
                          pivot: [0.5, 0.5],
                      },
                      components: [
                          {
                              type: 'image',
                              data: {
                                  cacheKey: `daily_gift${index}_bottom`,
                              },
                          },
                          {
                              type: 'visibility',
                              data: {
                                  hidden: this.loginStreak === index,
                              },
                          },
                      ],
                  },
                  {
                      name: 'day_gift_upper',
                      layout: {
                          anchors: [0.5, 0.51, 0.5, 0.51],
                          offsets: [0, 0, 0, 0],
                          pivot: [0.5, 0.5],
                      },
                      components: [
                          {
                              type: 'image',
                              data: {
                                  cacheKey: `daily_gift${index}_upper`,
                              },
                          },
                          {
                              type: 'visibility',
                              data: {
                                  hidden: this.loginStreak === index,
                              },
                          },
                      ],
                  },
                  this.addGiftDragonbonesContainer(),
              ];
    }

    private addDBAnimation(button: GameObject, index: number, loginStreak: number) {
        if (loginStreak === index) {
            const buttonDBContainer = button.find('DB_container');
            const buttonShine = GameObject.Instantiate(Armature, {
                dragonbonesName: 'daily_login_animations',
                armatureName: 'button_glare',
            });
            buttonDBContainer.addChild(buttonShine);

            const giftDBContainer = this.findChildByName('gift_DB_container');
            this.giftArmature = GameObject.Instantiate(
                Armature,
                this.isLastDay
                    ? {
                          dragonbonesName: 'daily_login_animations',
                          armatureName: 'day_7_idle',
                      }
                    : this.isPetDay
                    ? {
                          dragonbonesName: 'daily_login_animations',
                          armatureName: 'giftbox_pet',
                          animationName: 'open_idle',
                      }
                    : {
                          dragonbonesName: 'daily_login_animations',
                          armatureName: `giftbox_${index}`,
                          animationName: 'idle',
                      }
            );

            giftDBContainer.addChild(this.giftArmature);
        } else if (this.isPetDay) {
            const giftPetDBContainer = this.findChildByName('gift_DB_container');
            this.giftPetArmature = GameObject.Instantiate(Armature, {
                dragonbonesName: 'daily_login_animations',
                armatureName: 'giftbox_pet',
                animationName: 'idle',
            });

            giftPetDBContainer.addChild(this.giftPetArmature);
        }
        const checkDBContainer = this.findChildByName('check_DB_container');
        this.checkArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'daily_login_animations',
            armatureName: this.isLastDay ? 'day_7_check' : 'check',
            alpha: index < loginStreak ? 1 : 0,
            playTimes: 1,
        });
        checkDBContainer.addChild(this.checkArmature);
    }

    private addRewardsContainer(): IElementData {
        return {
            name: 'rewards',
            layout: {
                anchors: [0.5, 0.6, 0.5, 0.6],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
            },
            components: [
                {
                    type: 'visibility',
                    data: {
                        hidden: true,
                    },
                },
            ],
        };
    }

    private addGiftDragonbonesContainer(): IElementData {
        return {
            name: 'gift_DB_container',
            layout: {
                anchors: this.isLastDay ? [0.5, 0.585, 0.5, 0.585] : [0.5, 0.73, 0.5, 0.73],
                offsets: [0, 0, 0, 0],
                pivot: [0.5, 0.5],
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: 'blank',
                    },
                },
            ],
        };
    }
}
